import React, { useEffect, useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import posthog from 'posthog-js';
import log from 'loglevel';
import { IS_DEVELOPMENT } from 'api-config.mjs';
import { getSessionFromStorage, SessionContext } from 'components/Session';
import { LogRocketLazyLoader } from "components/LogRocketLazyLoader";
import { OpenReplayLazyLoader } from "components/OpenReplayLazyLoader";
// import { loadAdCritterScripts, parseAdvertisingQueryParams } from "components/AdCritter";
// import TawkMessengerReact from '@tawk.to/tawk-messenger-react';

const queryClient = new QueryClient();

// Identify a logged-in user to PostHog. No-op in development or when the session
// is not logged in. Shared by setSessionContext (on login) and the mount effect
// (for an already-seeded session) so the identify payload lives in one place.
// Docs: https://posthog.com/docs/getting-started/identify-users
const identifyPostHogUser = (session) => {
  if (IS_DEVELOPMENT) return;
  if (!session?.isLoggedIn) return;
  // Google logins never populate userInfo: useGoogleLogin (@react-oauth/google)
  // resolves a TokenResponse, which has no profileObj (that field belonged to the
  // deprecated react-google-login), so `userInfo: response.profileObj` is undefined.
  // Those users are still fully logged in and can pay — without this fallback they
  // stay anonymous in PostHog and their conversions cannot be attributed. The
  // server session carries the email for every login type.
  const { email, name, givenName, familyName } = session.userInfo || {};
  const serverUser = session.session?.user || {};
  const identifyEmail = email || serverUser.email;
  if (!identifyEmail) return;
  const displayName =
    name ||
    [givenName, familyName].filter(Boolean).join(" ") ||
    serverUser.name ||
    undefined;
  posthog.identify(identifyEmail, {
    email: identifyEmail,
    ...(displayName && { name: displayName }),
  });
  if (typeof clarity !== 'undefined') {
    clarity('identify', identifyEmail)
  }
};

const App = (props) => {
  const { children } = props;
  log.debug("App::render() props:", props);

  // Seed the session SYNCHRONOUSLY from localStorage so the very first client
  // render already knows whether the user is logged in. Previously this started
  // as {} and hydrated one render later in a useEffect, leaving a window where
  // sessionContext.isLoggedIn === undefined. Layout(requireLoggedIn) treats that
  // as logged-out — it swaps {children} for <LoadingPage> and navigates to
  // /login — which UNMOUNTS the page subtree and tears down any in-progress
  // Stripe Elements (the intermittent /subscriptions add-card "Could not find
  // Stripe expiry field" flake). getSessionFromStorage returns {} during SSR.
  const [ sessionContext, setSessionContextState ] = useState(getSessionFromStorage);
  const setSessionContext = (newSessionContext) => {
    localStorage.setItem("sessionContext", JSON.stringify(newSessionContext));
    setSessionContextState(newSessionContext);

    // PostHog: identify on login, reset on logout.
    identifyPostHogUser(newSessionContext);
    if (
      !IS_DEVELOPMENT &&
      !newSessionContext?.isLoggedIn &&
      Object.keys(newSessionContext).length === 0
    ) {
      posthog.reset();
    }
  };

  // Identify the already-seeded user to PostHog once on mount. We deliberately do
  // NOT call setSessionContext here — the state is already seeded synchronously
  // above, and re-setting it would churn the context identity on every page load,
  // re-rendering (and remounting) the whole tree, which is what tore down the
  // add-card Stripe Elements. (AdCritter init previously lived here; it is
  // currently commented out.)
  useEffect(() => {
    identifyPostHogUser(sessionContext);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <SessionContext.Provider value={{ sessionContext, setSessionContext }}>
        <QueryClientProvider client={queryClient}>
          <LogRocketLazyLoader />
          <OpenReplayLazyLoader />
          { children }
        </QueryClientProvider>
      </SessionContext.Provider>
      <div className="App">
        {/* <TawkMessengerReact
            propertyId="64730f81ad80445890ef84a0"
            widgetId="1h1gn94mi"/> */}
      </div>
    </>
  );
};

export default App;
